import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ToastService } from './toasts.service';
import * as requests from '../requests/index';
import { ShareSettings } from '../models/share-settings';
import { PlanogramSharedUserDTO, UserDTO } from '../models/user';
import { ShareType } from '../models/share-type';
import { BaseApiService } from './base-api.service';

@Injectable({
  providedIn: 'root',
})
export class ShareService extends BaseApiService {
  constructor(http: HttpClient, toastService: ToastService) {
    super(http, toastService);
  }

  synchronizePlanogram(planogramId: string, stateId: string): Observable<boolean> {
    return this.apiRequest<boolean>(
      'share/synchronize',
      'POST',
      new requests.SynchronizePlanogramRequest(planogramId, stateId),
      'Failed to synchronize planogram'
    ).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  resetPlanogramState(planogramId: string): Observable<string | null>{
    return this.apiRequest<string>(
      `share/reset-user-planogram-state/${planogramId}`,
      'GET',
      null,
      'Failed to reset planogram state'
    ).pipe(
      map((state) => state),
      catchError(() => of(null))
    );
  }

  getPlanogramShareSettings(planogramId: string): Observable<ShareSettings | null> {
    return this.apiRequest<ShareSettings>(
      `share/settings/${planogramId}`,
      'GET',
      null,
      'Failed to retrieve share settings'
    ).pipe(
      map((settings) => settings),
      catchError(() => of(null))
    );
  }

  addSharedUser(planogramId: string, stateId: string, user: UserDTO, shareType: ShareType): Observable<PlanogramSharedUserDTO | null> {
    return this.apiRequest<PlanogramSharedUserDTO>(
      'share/add-user',
      'POST',
      new requests.AddSharedUserRequest(planogramId, stateId, user, shareType),
      'Failed to add shared user'
    ).pipe(
      map((user) => user),
      catchError(() => of(null))
    );
  }

  removeSharedUser(planogramId: string, userId: string): Observable<boolean> {
    return this.apiRequest<boolean>(
      'share/remove-user',
      'POST',
      new requests.RemoveSharedUserRequest(planogramId, userId),
      'Failed to remove shared user'
    ).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  updateShareSettings(planogramId: string, settings: ShareSettings): Observable<boolean> {
    return this.apiRequest<boolean>(
      'share/update-settings',
      'POST',
      new requests.UpdateShareSettingsRequest(planogramId, settings),
      'Failed to update share settings'
    ).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  updateUserShareType(planogramId: string, stateId: string, targetUserId: string, shareType: ShareType): Observable<boolean> {
    return this.apiRequest<boolean>(
      'share/update-user-share-type',
      'POST',
      new requests.UpdateUserShareTypeRequest(planogramId, stateId, targetUserId, shareType),
      'Failed to update user share type'
    ).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }
}
